import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contact_Tasks } from "@/src/gql/graphql";
import { taskDetailActions } from "@/lib/actions/definitions/entities";

// ============================================================================
// ENHANCED TASK DETAIL PAGE CONFIGURATION
// ============================================================================

export const ENHANCED_TASK_DETAIL_CONFIG: DetailPageConfig<Contact_Tasks> = {
  entity: "tasks",
  displayName: "Task",

  // Tab configurations
  tabs: [
    {
      id: "details",
      label: "Details",
      icon: "file-text",
      variant: "blue",
      content: {
        type: "fields",
        layout: "grid",
        sections: [
          {
            id: "main-content",
            title: "Description",
            customComponent: "TaskDescriptionSection",
            layout: "full",
          },
          {
            id: "ai-summary",
            title: "AI Summary",
            customComponent: "TaskAISummary",
            customProps: {
              autoGenerate: false,
              showMetadata: true,
              showRefreshButton: true,
              showClearButton: false,
              compact: false,
            },
          },
          {
            id: "sidebar",
            title: "Task Information",
            customComponent: "TaskInformationSection",
            layout: "sidebar",
            gridColumns: 1,
          },
        ],
      },
    },
    {
      id: "notes",
      label: "Notes",
      icon: "message-square",
      variant: "orange",
      content: {
        type: "notes",
        customComponent: "TaskNotesDisplay",
        customProps: {
          showAddButton: true,
        },
      },
    },
    {
      id: "activity",
      label: "Activity",
      icon: "activity",
      variant: "green",
      content: {
        type: "activity",
        sections: [
          {
            id: "timeline",
            title: "Communication Timeline",
            customComponent: "TaskActivityTimeline",
          },
        ],
      },
    },
    {
      id: "company",
      label: "Company",
      icon: "building",
      variant: "red",
      content: {
        type: "related",
        sections: [
          {
            id: "company-info",
            title: "Company Information",
            customComponent: "TaskCompanyDisplay",
          },
        ],
      },
    },
    {
      id: "contact",
      label: "Contact",
      icon: "user",
      variant: "purple",
      content: {
        type: "related",
        sections: [
          {
            id: "contact-info",
            title: "Contact Information",
            customComponent: "TaskContactDisplay",
          },
        ],
      },
    },
  ],

  // Action configurations - from centralized registry
  actions: taskDetailActions,

  // Breadcrumb configuration
  breadcrumbs: {
    show: true,
    items: [
      { label: "Tasks", href: "/tasks" },
      {
        label: "Task Details",
        href: (task: Contact_Tasks) => `/tasks/${task.id}`,
        current: true,
      },
    ],
  },

  // Page configuration
  defaultTab: "details",
  showHeader: true,
  showBreadcrumbs: true,
  showActions: true,

  // Title and description templates
  titleTemplate: "{title}",
  descriptionTemplate: "Task Details",
};

// ============================================================================
// TASK-SPECIFIC CUSTOM COMPONENTS CONFIGURATION
// ============================================================================

export const TASK_DETAIL_TAB_COMPONENTS = {
  TaskAISummary: "TaskAISummary",
  TaskNotesDisplay: "TaskNotesDisplay",
  TaskActivityTimeline: "TaskActivityTimeline",
  TaskCompanyDisplay: "TaskCompanyDisplay",
  TaskContactDisplay: "TaskContactDisplay",
  ContactInfoDisplay: "ContactInfoDisplay",
};

export const TASK_DETAIL_ACTION_HANDLERS = {
  completeTask: async (task: Contact_Tasks, context: any) => {
    await context.onUpdateItem({ status: "completed" });
    context.onToast("Task completed successfully", "success");
  },

  duplicateTask: async (task: Contact_Tasks, context: any) => {
    context.onNavigate(`/tasks/create?duplicate=${task.id}`);
  },

  sendEmail: async (task: Contact_Tasks, context: any) => {
    // Generate email body with task details
    const taskDetails = [
      `Task: ${task.title}`,
      `Status: ${task.status}`,
      `Priority: ${task.priority}`,
    ];

    if (task.due_date) {
      taskDetails.push(`Due Date: ${new Date(task.due_date).toLocaleDateString()}`);
    }

    if (task.description) {
      taskDetails.push(`Description: ${task.description}`);
    }

    const emailBody = `Hi ${task.contacts?.name || "there"},

I wanted to follow up on the following task:

${taskDetails.map((detail) => `• ${detail}`).join("\n")}

Please let me know if you have any questions or need any clarification.

Best regards,`;

    // Set up compose modal data
    const composeData = {
      initialClient: task.contacts
        ? {
            id: task.contacts.id,
            name: task.contacts.name,
            email: task.contacts.email,
            company_name: task.contacts.company_name,
          }
        : undefined,
      initialSubject: `Re: Task - ${task.title}`,
      initialBody: emailBody,
    };

    context.onOpenModal("compose-email", {
      item: task,
      taskId: task.id,
      prefillData: composeData,
    });
    // Note: The compose modal would need to be configured to receive this data
  },
};
